/**
 * CurrencyContext — Cache del tipo de cambio USD/PYG en cliente.
 *
 * Se inicializa al cargar la app, refresca cada 30 min.
 * Si la API falla, usa un fallback razonable (7,200 PYG/USD ~ valor 2026).
 */
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../api/client.js";
import { useAuth } from "./AuthContext.jsx";

const CurrencyContext = createContext(null);

const FALLBACK_USD_TO_PYG = 7300;  // valor de mercado aproximado 2026
const REFRESH_MS = 30 * 60 * 1000; // 30 min

export function CurrencyProvider({ children }) {
  const [usdToPyg, setUsdToPyg] = useState(FALLBACK_USD_TO_PYG);
  const [fecha, setFecha] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const { miembro } = useAuth();

  // Se pide recién con sesión iniciada: antes salía al cargar la app, sin token, y daba 401 en cada pantalla.
  useEffect(() => {
    if (!miembro) return;
    const load = async () => {
      try {
        const r = await api.getTipoCambioActual("USD");
        if (r?.valor_pyg) {
          setUsdToPyg(parseFloat(r.valor_pyg));
          setFecha(r.fecha);
        }
      } catch {/* keep fallback */}
      finally { setLoaded(true); }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [miembro?.id]);

  return (
    <CurrencyContext.Provider value={{ usdToPyg, fecha, loaded }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext) || { usdToPyg: FALLBACK_USD_TO_PYG, loaded: false };
}

/**
 * Convierte un monto a USD y PYG.
 */
export function convertToBoth(amount, currency = "PYG", usdToPyg = FALLBACK_USD_TO_PYG) {
  const n = parseFloat(amount) || 0;
  if (n === 0) return { usd: 0, pyg: 0 };
  if (currency === "USD") return { usd: n, pyg: n * usdToPyg };
  if (currency === "PYG") return { usd: n / usdToPyg, pyg: n };
  return { usd: 0, pyg: 0 };
}

/**
 * Formatos para mostrar.
 */
export function formatUSD(n, options = {}) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const { compact = false } = options;
  if (compact && Math.abs(n) >= 1000) {
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    return `$${(n / 1000).toFixed(1)}K`;
  }
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function formatPYG(n, options = {}) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const { compact = false } = options;
  if (compact && Math.abs(n) >= 1000) {
    if (Math.abs(n) >= 1_000_000_000) return `Gs ${(n / 1_000_000_000).toFixed(1)}B`;
    if (Math.abs(n) >= 1_000_000)     return `Gs ${(n / 1_000_000).toFixed(1)}M`;
    return `Gs ${Math.round(n / 1000)}k`;
  }
  return `Gs ${Math.round(n).toLocaleString("es-PY")}`;
}
